import React, { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Navigate, Route, Routes, useLocation } from 'react-router'
import {
  DepositLimit,
  PasswordConfirm,
  PersonalDetails,
  SecuritySettings,
} from '@mobi/account/Areas/SignUp/Components'
import {
  Passport,
  IdentityDocumentSelector,
  DriversLicence,
  PhoneNumber,
  type MfaVerificationPage,
} from '@mobi/account/Areas/SignUp/Components/Verification'
import { FailedVerification } from './Message/FailedVerification'
import { LogonRequestData, LogonResult } from '@mobi/api-types'
import { routes, SIGNUP_ROOT_ROUTE, subRoutes } from '../Utils/routes'
import { useRiskAssessment } from '../Hooks/useRiskAssessment'
import { SignUpCompletePage } from './SignUpCompletePage'
import type { Campaign } from '../typings/types'
import { getSignUpState, setSignupNewSession } from '../Store'
import { useScrollRestoration } from '@mobi/account/Hooks/useScrollRestoration'
import { AdditionalInformation } from './AdditionalInformation'
import { VerificationAccountInformationPage } from '../../VerificationAccount/'
import { LocationDetails } from './LocationDetails'
import { ContactDetails } from './ContactDetails'

type SignUpPageProps = {
  isLoggedIn: boolean | null
  attemptLogin: (data: LogonRequestData) => Promise<
    LogonResult & {
      errorMessage?: string
    }
  >
  showLogin: (options?: { onLoginSuccessRedirectUrl?: string }) => void
  campaign: Campaign | undefined
  openDeposit: () => void
  onMount?: () => void
  onDispose?: () => void
  MfaVerificationPage: React.MemoExoticComponent<MfaVerificationPage>
}

const applicationRequiredRoutes: string[] = [
  routes.additionalInformation,
  routes.verificationOptions,
  routes.driversLicence,
  routes.passport,
  routes.password,
]

const verificationRequiredRoutes: string[] = [
  routes.phoneNumberVerification,
  routes.security,
  routes.depositLimit,
  routes.signUpComplete,
]

/** Redirects to personalDetails if firstName is not set */
const LocationDetailsGuard = ({
  firstName,
  children,
}: {
  firstName: string
  children: React.ReactNode
}) => {
  const location = useLocation()
  if (location.pathname === routes.locationDetails && !firstName) {
    return <Navigate to={routes.personalDetails} />
  }
  return <>{children}</>
}

/** Redirects to personalDetails if residentialAddress is not set */
const ContactDetailsGuard = ({
  residentialAddress,
  children,
}: {
  residentialAddress: unknown
  children: React.ReactNode
}) => {
  const location = useLocation()
  if (location.pathname === routes.contactDetails && !residentialAddress) {
    return <Navigate to={routes.personalDetails} />
  }
  return <>{children}</>
}

/** Redirects to personalDetails if applicationReferenceNumber is not set */
const ApplicationGuard = ({
  applicationReferenceNumber,
  children,
}: {
  applicationReferenceNumber: number
  children: React.ReactNode
}) => {
  const location = useLocation()
  if (applicationRequiredRoutes.includes(location.pathname) && !applicationReferenceNumber) {
    return <Navigate to={routes.personalDetails} />
  }
  return <>{children}</>
}

/** Redirects to home if identity is not verified */
const VerificationGuard = ({
  isVerified,
  children,
}: {
  isVerified: boolean
  children: React.ReactNode
}) => {
  const location = useLocation()
  if (verificationRequiredRoutes.includes(location.pathname) && !isVerified) {
    return <Navigate to={routes.home} />
  }
  return <>{children}</>
}

/** Redirects logged-in users away from application required routes */
const LoggedInGuard = ({
  isLoggedIn,
  children,
}: {
  isLoggedIn: boolean | null
  children: React.ReactNode
}) => {
  const location = useLocation()
  if (applicationRequiredRoutes.includes(location.pathname) && isLoggedIn) {
    return <Navigate to={routes.home} />
  }
  return <>{children}</>
}

export const SignUpPage = ({
  isLoggedIn,
  attemptLogin,
  showLogin,
  campaign,
  openDeposit,
  onMount,
  onDispose,
  MfaVerificationPage,
}: SignUpPageProps) => {
  const dispatch = useDispatch()
  const [newPassword, setNewPassword] = React.useState('')
  const state = useSelector(getSignUpState)
  useRiskAssessment()

  useScrollRestoration()

  useEffect(() => {
    onMount?.()
    return () => {
      dispatch(setSignupNewSession())
      onDispose?.()
    }
  }, [dispatch])

  return (
    <LoggedInGuard isLoggedIn={isLoggedIn}>
      <LocationDetailsGuard firstName={state.signupApplication.firstName}>
        <ContactDetailsGuard residentialAddress={state.signupApplication.residentialAddress}>
          <ApplicationGuard
            applicationReferenceNumber={state.signupApplication.applicationReferenceNumber}
          >
            <VerificationGuard isVerified={state.signupVerification.isVerified}>
              <Routes>
                <Route path='/' element={<PersonalDetails />} />
                <Route path={subRoutes.locationDetails} element={<LocationDetails />} />
                <Route path={subRoutes.contactDetails} element={<ContactDetails />} />
                <Route path={subRoutes.additionalInformation} element={<AdditionalInformation />} />
                <Route
                  path={subRoutes.verificationOptions}
                  element={<IdentityDocumentSelector />}
                />
                <Route path={subRoutes.driversLicence} element={<DriversLicence />} />
                <Route path={subRoutes.passport} element={<Passport />} />
                <Route
                  path={subRoutes.password}
                  element={
                    <PasswordConfirm
                      onCompleted={password => {
                        setNewPassword(password)
                      }}
                      attemptLogin={attemptLogin}
                    />
                  }
                />
                <Route
                  path={subRoutes.phoneNumberVerification}
                  element={<PhoneNumber MfaVerificationPage={MfaVerificationPage} />}
                />
                <Route
                  path={subRoutes.security}
                  element={<SecuritySettings password={newPassword} />}
                />
                <Route
                  path={subRoutes.signUpUnsuccessful}
                  element={<FailedVerification showLogin={showLogin} />}
                />
                <Route
                  path={subRoutes.signUpComplete}
                  element={<SignUpCompletePage campaign={campaign} openDeposit={openDeposit} />}
                />
                <Route path={subRoutes.depositLimit} element={<DepositLimit />} />
                <Route
                  path={subRoutes.verificationAccountInformation}
                  element={<VerificationAccountInformationPage />}
                />
                <Route path='*' element={<Navigate to={`/${SIGNUP_ROOT_ROUTE}`} />} />
              </Routes>
            </VerificationGuard>
          </ApplicationGuard>
        </ContactDetailsGuard>
      </LocationDetailsGuard>
    </LoggedInGuard>
  )
}

SignUpPage.displayName = 'SignUpPage'

export default SignUpPage
